import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';
import ApplyResetWrapper from '@/components/filters/ApplyResetWrapper';
import type { Filter, MeasurementUnit } from '@cvent/venue-search-shared/types';
import type { SpaceOptions } from '@/utils/filters/options/getSpaceOptions';
import dynamic from 'next/dynamic';
import { shouldVisible } from '@/utils/filters/filterHelper';
import { type RoomLayout } from '@/utils/filters/options/getRoomLayoutOptions';
import Loader from '@/components/filters/Loader';
import type { Locale } from '@/i18n';
import { formatMessage } from '@/utils/translationHelper';

const SpaceMeasurementFilter = dynamic(() => import('@/components/filters/SpaceMeasurementFilter'), {
  ssr: false,
  loading: () => <Loader />
});

type Props = {
  content: SpaceOptions;
  appliedFilters: Filter;
  locale: Locale;
  applicableFilters: Array<string>;
  labels: {
    maxMinError: string;
    wholeNumberRequired: string;
    howMuchSpace: string;
    calculateCapacity: string;
    allFilters: string;
    resetAllFilters: string;
    cancelAllFilters: string;
    applyAllFilters: string;
    clear: string;
    apply: string;
    close: string;
    back: string;
  };
  roomLayoutOptions: Array<RoomLayout>;
  measurementUnit: MeasurementUnit;
  errorLabels: Record<string, string>;
  setSortingLabel?: Dispatch<SetStateAction<string>>;
  setSortingType?: Dispatch<SetStateAction<string>>;
  sortOptions?: { label: string; value: string }[];
  sortType?: string;
  sort?: string;
  setSort?: Dispatch<SetStateAction<string>>;
  searchTerm: string;
  setIsRfpBannerVisible: (isRfpBannerVisible: boolean) => void;
  testID?: string;
  isSearchExpanded: boolean;
  setIsSearchExpanded: (v: boolean) => void;
};

export default function SpaceMeasurementContainer({
  content,
  appliedFilters,
  locale,
  applicableFilters,
  labels,
  setSortingLabel,
  sortType,
  setSortingType,
  setSort,
  sortOptions,
  sort,
  roomLayoutOptions,
  measurementUnit,
  errorLabels,
  searchTerm,
  setIsRfpBannerVisible,
  testID,
  isSearchExpanded,
  setIsSearchExpanded
}: Props) {
  const {
    maxMinError,
    wholeNumberRequired,
    howMuchSpace,
    calculateCapacity,
    allFilters,
    resetAllFilters,
    cancelAllFilters,
    applyAllFilters,
    clear,
    apply,
    close,
    back
  } = labels;
  const [selected, setSelected] = useState<Filter>(appliedFilters);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [filterCount, setFilterCount] = useState<number>(0);

  useEffect(() => {
    setSelected(appliedFilters);

    // Calculating filter count to be used on surface filter
    let count = appliedFilters.TOTAL_MEETING_SPACE?.minimum || appliedFilters.TOTAL_MEETING_SPACE?.maximum ? 1 : 0;
    count += appliedFilters.LARGEST_ROOM?.minimum || appliedFilters.LARGEST_ROOM?.maximum ? 1 : 0;
    count += appliedFilters.SECOND_LARGEST_ROOM?.minimum || appliedFilters.SECOND_LARGEST_ROOM?.maximum ? 1 : 0;
    setFilterCount(count);
  }, [appliedFilters]);

  const measurementOptions = content.values.filter(v => {
    // ceiling height and exhibit space not needed in outer filter
    if (v.value === 'CEILING_HEIGHT' || v.value === 'EXHIBIT_SPACE') {
      return false;
    }
    return !!(applicableFilters.includes(v.value) || selected[v.value]);
  });

  return (
    shouldVisible(['SECOND_LARGEST_ROOM', 'TOTAL_MEETING_SPACE', 'LARGEST_ROOM'], applicableFilters, selected) &&
    measurementOptions.length > 0 && (
      <ApplyResetWrapper
        setIsSearchExpanded={setIsSearchExpanded}
        isSearchExpanded={isSearchExpanded}
        title={formatMessage(content.title, locale, {
          count: filterCount
        })}
        selected={selected}
        setSelected={setSelected}
        appliedFilters={appliedFilters}
        filters={['SECOND_LARGEST_ROOM', 'TOTAL_MEETING_SPACE', 'LARGEST_ROOM']}
        testID={testID}
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        strings={{ allFilters, resetAllFilters, cancelAllFilters, applyAllFilters, clear, apply }}
        setSortingLabel={setSortingLabel}
        setSortingType={setSortingType}
        sortOptions={sortOptions}
        sortType={sortType}
        sort={sort}
        setSort={setSort}
        searchTerm={searchTerm}
        filterCount={filterCount}
        setIsRfpBannerVisible={setIsRfpBannerVisible}
      >
        <SpaceMeasurementFilter
          options={measurementOptions}
          selected={selected}
          setSelected={setSelected}
          labels={{
            title: content.title,
            subTitle: content.subTitle as string,
            close,
            back,
            maxMinError,
            wholeNumberRequired,
            howMuchSpace,
            calculateCapacity
          }}
          showTitle={false}
          showCalculateCapacity={false}
          isSurfaceFilter
          roomLayoutOptions={roomLayoutOptions}
          measurementUnit={measurementUnit}
          errorLabels={errorLabels}
          testID={testID}
        />
      </ApplyResetWrapper>
    )
  );
}
